import React, { useState, useEffect, useRef } from 'react'
import { IconHeart } from './Icons'

export function MiniLive({ hr, color, label, samples, min, max, time, delay, theme = 'dark' }) {
  const rootRef = useRef(null)
  const [dims, setDims] = useState({ w: 220, h: 140 })

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const update = () => {
      if (el) {
        setDims({
          w: el.offsetWidth || window.innerWidth || 220,
          h: el.offsetHeight || window.innerHeight || 140,
        })
      }
    }
    update()
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update)
      ro.observe(el)
    }
    window.addEventListener('resize', update)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [])

  const isLight = theme === 'light'
  const c = color || '#ff3b30'
  const vals = (samples || []).map(s => s.hr).filter(h => h != null && h > 0).slice(-90)
  const lo = vals.length ? Math.min(...vals) : 0
  const hi = vals.length ? Math.max(...vals) : 1
  const range = (hi - lo) || 1
  const W = 100, H = 30
  const pts = vals.map((v, i) => {
    const x = vals.length > 1 ? (i / (vals.length - 1)) * W : 0
    const y = H - ((v - lo) / range) * (H - 4) - 2
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')

  const textColor = isLight ? '#0f172a' : '#ffffff'
  const subTextColor = isLight ? '#64748b' : '#9a9aa0'
  const bg = isLight
    ? `radial-gradient(120% 130% at 50% -10%, ${c}16, #ffffff 68%)`
    : `radial-gradient(120% 130% at 50% -10%, ${c}26, #0b0b0c 68%)`

  // ── Dimensiones dinámicas y adaptabilidad al tamaño mínimo ──────────────────
  const isHorizontal  = dims.w >= dims.h * 1.45 && dims.h <= 120
  const isCompact     = dims.h < 118 || dims.w < 175
  const isTiny        = dims.h < 90  || dims.w < 140
  const showDelay     = !isHorizontal && dims.h >= 115 && dims.w >= 165
  const showSparkline = dims.h >= 65

  // En formato horizontal se calcula en base al ALTO para que el número sea GIGANTE
  const hrFontSize    = isHorizontal
    ? Math.max(26, Math.min(62, Math.round(dims.h * 0.72)))
    : Math.max(22, Math.min(64, Math.round(dims.h * 0.42), Math.round(dims.w * 0.29)))

  const heartSize     = Math.max(12, Math.min(26, Math.round(hrFontSize * 0.42)))
  const sparkHeight   = showSparkline ? Math.max(12, Math.min(38, Math.round(dims.h * (isHorizontal ? 0.35 : 0.22)))) : 0
  const padV          = Math.max(2, Math.min(11, Math.round(dims.h * 0.08)))
  const padH          = Math.max(6, Math.min(14, Math.round(dims.w * 0.06)))

  // ── Vista Horizontal / Apaisada (elementos lado a lado con número enorme) ──
  if (isHorizontal) {
    return (
      <div
        ref={rootRef}
        style={{
          width: '100%', height: '100%',
          display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
          padding: `0 ${padH}px`, color: textColor,
          background: bg,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          boxSizing: 'border-box',
          overflow: 'hidden',
          gap: 10,
        }}
      >
        {/* Lado izquierdo: Corazón latiendo + Número GIGANTE + bpm */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <span style={{ display: 'inline-block', animation: hr ? 'pip-pulse 1s ease-in-out infinite' : 'none' }}>
            <IconHeart size={heartSize} color={c} fill={c} />
          </span>
          <span style={{
            fontSize: hrFontSize,
            fontWeight: 900,
            lineHeight: 1,
            color: c,
            fontVariantNumeric: 'tabular-nums',
            textShadow: isLight ? `0 2px 10px ${c}33` : `0 0 24px ${c}66`
          }}>
            {hr ?? '–'}
          </span>
          <span style={{ fontSize: Math.max(9, Math.round(hrFontSize * 0.26)), color: subTextColor, fontWeight: 700, alignSelf: 'flex-end', marginBottom: 2 }}>
            bpm
          </span>
        </div>

        {/* Lado derecho: EN VIVO + hora + Sparkline */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', flex: 1, minWidth: 60, height: '100%', gap: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: c, boxShadow: `0 0 6px ${c}`, display: 'inline-block', animation: 'pip-blink 1.6s ease-in-out infinite' }} />
            <span style={{ fontSize: 9, fontWeight: 800, color: c, letterSpacing: 0.8 }}>{(label || 'EN VIVO').toUpperCase()}</span>
            <span style={{ fontSize: 9.5, color: subTextColor, fontVariantNumeric: 'tabular-nums', marginLeft: 2 }}>{time}</span>
          </div>

          {vals.length > 1 && showSparkline && (
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', maxWidth: 110, height: sparkHeight, display: 'block' }}>
              <defs>
                <linearGradient id="pipFillH" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={c} stopOpacity={isLight ? 0.25 : 0.45} />
                  <stop offset="100%" stopColor={c} stopOpacity="0" />
                </linearGradient>
              </defs>
              <polygon points={`0,${H} ${pts} ${W},${H}`} fill="url(#pipFillH)" />
              <polyline points={pts} fill="none" stroke={c} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            </svg>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      style={{
        width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        padding: `${padV}px ${padH}px 0`, color: textColor,
        background: bg,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Barra superior */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: 4 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: isTiny ? 4 : 6, fontSize: isTiny ? 8 : (isCompact ? 9 : 10), fontWeight: 800, letterSpacing: isTiny ? 0.5 : 1, color: c }}>
          <span style={{ width: isTiny ? 5 : 7, height: isTiny ? 5 : 7, borderRadius: '50%', background: c, boxShadow: `0 0 8px ${c}`, display: 'inline-block', animation: 'pip-blink 1.6s ease-in-out infinite' }} />
          {(label || 'EN VIVO').toUpperCase()}
        </span>
        <span style={{ fontSize: isTiny ? 9 : (isCompact ? 10 : 11), color: subTextColor, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {time}
        </span>
      </div>

      {/* Lectura de BPM y corazón */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isCompact ? 5 : 7, flex: 1, minHeight: 0 }}>
        <span style={{ display: 'inline-block', animation: hr ? 'pip-pulse 1s ease-in-out infinite' : 'none', flexShrink: 0 }}>
          <IconHeart size={heartSize} color={c} fill={c} />
        </span>
        <span style={{
          fontSize: hrFontSize,
          fontWeight: 800,
          lineHeight: 1,
          color: c,
          fontVariantNumeric: 'tabular-nums',
          textShadow: isLight ? `0 2px 10px ${c}33` : `0 0 24px ${c}66`
        }}>
          {hr ?? '–'}
        </span>
        <span style={{ fontSize: isCompact ? 11 : 14, color: subTextColor, fontWeight: 600, alignSelf: 'flex-end', marginBottom: isCompact ? 2 : 5 }}>
          bpm
        </span>
      </div>

      {/* Retraso (se oculta en tamaños mínimos para no quitar espacio al número) */}
      {showDelay && (
        <div style={{ display: 'flex', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: subTextColor, fontVariantNumeric: 'tabular-nums', textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0, marginBottom: 2 }}>
          Retraso: {delay ?? 0}s vs tiempo real
        </div>
      )}

      {/* Sparkline de tendencia */}
      {showSparkline && (
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: sparkHeight, display: 'block', flexShrink: 0 }}>
          <defs>
            <linearGradient id="pipFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={c} stopOpacity={isLight ? 0.2 : 0.4} />
              <stop offset="100%" stopColor={c} stopOpacity="0" />
            </linearGradient>
          </defs>
          {vals.length > 1 && <polygon points={`0,${H} ${pts} ${W},${H}`} fill="url(#pipFill)" />}
          {vals.length > 1 && <polyline points={pts} fill="none" stroke={c} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
        </svg>
      )}
    </div>
  )
}
